import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import {
  getPostAuthorDisplay, getVideoPreviewUrl, type Post, type PostMedia,
} from '../../utils/communityUtils';

type Props = {
  posts: Post[];
  loading: boolean;
  hasMore: boolean;
  loadError: boolean;
  onRetry: () => void;
  liked: Record<string, boolean>;
  busyLikes: Record<string, boolean>;
  followedIds: string[];
  onLike: (id: string) => void;
  onReply: (post: Post) => void;
  onShare: (post: Post) => void;
  onFollow: (id: string) => void;
  onNearEnd: () => void;
  onCreateVideo: () => void;
};
type Clip = { post: Post; media: PostMedia };

export default function CommunityVideoFeed({
  posts, loading, hasMore, loadError, onRetry, liked, busyLikes, followedIds, onLike, onReply, onShare, onFollow, onNearEnd, onCreateVideo,
}: Props) {
  const clips = useMemo<Clip[]>(() =>
    posts.flatMap(post => (post.media || [])
      .filter(media => media.media_type === 'video')
      .map(media => ({ post, media }))), [posts]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const players = useRef<Map<string, HTMLVideoElement>>(new Map());
  const openButton = useRef<HTMLButtonElement | null>(null);
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const lastActive = useRef(0);
  const resumeAt = useRef<{ key: string; seconds: number } | null>(null);
  const nearEnd = useRef(onNearEnd);
  const wheelGesture = useRef({ lastMoveAt: 0, delta: 0 });
  // Explicit playback must be invoked during a Safari user gesture.
  const manuallyStarted = useRef<string | null>(null);
  nearEnd.current = onNearEnd;

  const [active, setActive] = useState(0);
  const [immersive, setImmersive] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [paused, setPaused] = useState(false);
  const [expandedCaption, setExpandedCaption] = useState<string | null>(null);
  const [visibleStack, setVisibleStack] = useState<string | null>(null);
  const [replayToken, setReplayToken] = useState(0);
  const [needsTap, setNeedsTap] = useState<string | null>(null);
  const [unsupportedVideo, setUnsupportedVideo] = useState<string | null>(null);

  // On iPhone, an asynchronous React state update cannot replace calling
  // play() from the actual tap event. Apple also blocks muted autoplay in
  // Low Power Mode, so reveal a working manual control when that happens.
  const attemptPlayback = useCallback((video: HTMLVideoElement, key: string) => {
    if (!video.paused && !video.ended) {
      setNeedsTap(current => current === key ? null : current);
      return;
    }
    try {
      const result = video.play();
      if (result && typeof result.then === 'function') {
        void result.then(() => {
          setNeedsTap(current => current === key ? null : current);
        }).catch(() => {
          if (video.isConnected && video.paused) setNeedsTap(key);
        });
      }
    } catch {
      setNeedsTap(key);
    }
  }, []);

  const toggleDirectPlayback = (key: string) => {
    const video = players.current.get(key);
    if (!video) {
      setNeedsTap(key);
      return;
    }
    if (!video.paused && !video.ended) {
      video.pause();
      setPaused(true);
      setNeedsTap(key);
      return;
    }
    // Playback controls work on every publicly visible video.
    video.muted = !soundOn;
    manuallyStarted.current = key;
    setPaused(false);
    attemptPlayback(video, key);
  };

  // Let Safari scroll the document naturally, including back to the header.
  // Page-level scroll snapping was pulling upward swipes back to a video card.
  const moveTo = useCallback((index: number, behavior: ScrollBehavior = 'smooth') => {
    const host = scrollRef.current;
    if (!host || !clips.length) return;
    const clamped = Math.min(Math.max(0, index), clips.length - 1);
    const target = host.querySelector<HTMLElement>('[data-video-index="' + clamped + '"]');
    if (target) host.scrollTo({ top: target.offsetTop, behavior });
  }, [clips.length]);

  // Desktop mice and trackpads advance one video per deliberate downward or
  // upward scroll. Handle wheel events on the actual full-screen scroller,
  // rather than the page (which is locked while the dialog is open).
  useEffect(() => {
    if (!immersive) return;
    const scroller = scrollRef.current;
    if (!scroller) return;
    wheelGesture.current = { lastMoveAt: 0, delta: 0 };

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey || !event.deltaY) return;
      // Long descriptions have their own scrollbar.
      const target = event.target;
      if (target instanceof Element && target.closest('.v5-reel-caption-sheet')) return;
      event.preventDefault();
      const gesture = wheelGesture.current;
      const now = Date.now();
      if (now - gesture.lastMoveAt < 460) {
        gesture.delta = 0;
        return;
      }
      const pixels = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroller.clientHeight : 1);
      gesture.delta += pixels;
      if (Math.abs(gesture.delta) < 24) return;
      const direction = gesture.delta > 0 ? 1 : -1;
      gesture.lastMoveAt = now;
      gesture.delta = 0;
      const current = Math.min(clips.length - 1,
        Math.max(0, Math.round(scroller.scrollTop / Math.max(1, scroller.clientHeight))));
      moveTo(current + direction);
    };

    scroller.addEventListener('wheel', onWheel, { passive: false });
    return () => scroller.removeEventListener('wheel', onWheel);
  }, [immersive, moveTo, clips.length]);

  // The page itself scrolls through a stack of video cards. Only the most
  // visible clip plays; videos outside the viewport pause to save bandwidth.
  useEffect(() => {
    const host = stackRef.current;
    if (!host || immersive || typeof IntersectionObserver === 'undefined') return;
    const ratios = new Map<string, number>();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const key = (entry.target as HTMLElement).dataset.stackVideo;
        if (key) ratios.set(key, entry.isIntersecting ? entry.intersectionRatio : 0);
      });
      const ranked = [...ratios.entries()].sort((a,b) => b[1]-a[1]);
      setVisibleStack(ranked[0] && ranked[0][1] >= 0.45 ? ranked[0][0] : null);
    }, {threshold:[0,.25,.45,.6,.8]});
    host.querySelectorAll('[data-stack-video]').forEach(item=>observer.observe(item));
    return ()=>observer.disconnect();
  }, [clips.length,immersive]);

  useEffect(() => {
    if (immersive) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    for (const [key, video] of players.current) {
      const index = clips.findIndex(clip => clip.media.media_id === key);
      video.muted = !soundOn;
      const inView = key === visibleStack && !paused && document.visibilityState === 'visible';
      if (!inView) {
        video.pause();
      } else if (reduced && manuallyStarted.current !== key) {
        // Respect reduced-motion preferences, but allow a direct tap to play.
        video.pause();
        setNeedsTap(key);
      } else {
        attemptPlayback(video, key);
      }
    }
  }, [visibleStack, immersive, soundOn, paused, replayToken, clips, attemptPlayback ]);

  useEffect(() => {
    if (immersive || !hasMore || loading || loadError) return;
    const host = stackRef.current;
    const sentinel = host?.querySelector('[data-load-sentinel]');
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) nearEnd.current();
    },{rootMargin:'800px 0px'});
    observer.observe(sentinel);
    return()=>observer.disconnect();
  },[immersive,clips.length,hasMore,loading,loadError ]);

  useEffect(() => {
    const host = scrollRef.current;
    if (!host) return;
    // Opening and closing the immersive player should keep the same clip in view.
    moveTo(lastActive.current, 'auto');
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      const mostVisible = entries.filter(entry => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!mostVisible) return;
      const index = Number((mostVisible.target as HTMLElement).dataset.videoIndex);
      if (!Number.isInteger(index)) return;
      if (lastActive.current !== index) {
        manuallyStarted.current = null;
        setPaused(false);
        setNeedsTap(null);
      }
      lastActive.current = index;
      setActive(index);
      if (index >= clips.length - 2) nearEnd.current();
    }, { root: host, threshold: [0.65, 0.85] });
    host.querySelectorAll('[data-video-index]').forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }, [immersive, clips.length, moveTo]);

  useEffect(() => {
    if (!immersive) return;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const sync = () => {
      clips.forEach(({ media }, index) => {
        const video = players.current.get(media.media_id);
        if (!video) return;
        video.muted = !soundOn;
        if (index !== active || paused || document.visibilityState !== 'visible') {
          video.pause();
        } else if (reducedMotion && manuallyStarted.current !== media.media_id) {
          video.pause();
          setNeedsTap(media.media_id);
        } else {
          attemptPlayback(video, media.media_id);
        }
      });
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      players.current.forEach(video => video.pause());
    };
  }, [clips, active, paused, soundOn, immersive, attemptPlayback ]);

  useEffect(() => {
    if (!immersive) return;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus({ preventScroll: true });
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setImmersive(false);
        return;
      }
      // Keep keyboard focus in the immersive dialog.
      if (event.key === 'Tab' && overlayRef.current) {
        const elements = Array.from(overlayRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex="0"]',
        )).filter(element => element.offsetParent !== null);
        const first = elements[0], last = elements[elements.length - 1];
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = oldOverflow;
      document.removeEventListener('keydown', onKeyDown);
      window.requestAnimationFrame(() => openButton.current?.focus({ preventScroll: true }));
    };
  }, [immersive]);

  function keepPosition(clip: Clip) {
    const video = players.current.get(clip.media.media_id);
    if (video) resumeAt.current = { key: clip.media.media_id, seconds: video.currentTime };
  }
  function expand(index: number) {
    const clip = clips[index];
    if (!clip) return;
    keepPosition(clip);
    lastActive.current = index;
    setActive(index);
    setExpandedCaption(null);
    setImmersive(true);
    setPaused(false);
  }
  function collapse() {
    const clip = clips[active];
    if (clip) keepPosition(clip);
    setImmersive(false);
    setExpandedCaption(null);
  }
  function openReply(post: Post) {
    if (immersive) setImmersive(false);
    onReply(post);
  }

  const stacked = (
    <div className="v5-video-stack" ref={stackRef} aria-label="Videos stacked in a continuous scrolling feed">
      {clips.map(({post,media}) => {
        const key=media.media_id;
        const author=getPostAuthorDisplay(post);
        const caption=[post.title,post.content].filter(Boolean).join(' ');
        const active=visibleStack===key;
        return <article key={key} className="v5-video-stack-card" data-stack-video={key}>
          <div className="v5-video-stack-media">
            <video ref={element=>{if(element)players.current.set(key,element);else players.current.delete(key)}}
              src={getVideoPreviewUrl(media.media_url)} preload={clips[0]?.media.media_id===key || active ? 'metadata':'none'}
              muted={!soundOn} playsInline autoPlay loop onClick={()=>expand(clips.findIndex(c=>c.media.media_id===key))}
              aria-label={'Watch entire video from '+author.name}/>
            {needsTap===key && <button type="button" className="v5-reel-play" onClick={()=>toggleDirectPlayback(key)} aria-label="Play grocery video">▶</button>}
            <div className="v5-reel-top"><span className="v5-reel-topic">{post.video_topic||'Grocery finds'}</span>
              <button type="button" onClick={()=>setSoundOn(v=>!v)}>{soundOn?'🔊 Sound on':'🔇 Sound off'}</button></div>
            <button type="button" className="v5-stack-expand" onClick={()=>expand(clips.findIndex(c=>c.media.media_id===key))}>⛶ Watch full video</button>
            <div className="v5-reel-caption-min"><strong>{author.name}</strong><p><span>{caption||'Grocery video'}</span>
              {caption.length>60&&<button type="button" onClick={()=>setExpandedCaption(expandedCaption===key?null:key)}>{expandedCaption===key?'Less':'More'}</button>}</p>
              {expandedCaption===key&&<p className="v5-stack-full-caption">{caption}</p>}
            </div>
            <div className="v5-reel-actions">
              <button type="button" disabled={!!busyLikes[post.post_id]}
                aria-pressed={!!liked[post.post_id]}
                onClick={() => onLike(post.post_id)}
                aria-label={liked[post.post_id] ? 'Unlike video' : 'Like video'}>
                <span aria-hidden="true">{liked[post.post_id] ? '♥' : '♡'}</span><small>{post.likes_count} Likes</small>
              </button>
              <button type="button" onClick={()=>onReply(post)} aria-label="Reply">↪<small>{post.comments_count} Replies</small></button>
              <button type="button" onClick={()=>onShare(post)} aria-label="Share">↗<small>Share</small></button>
              <button type="button" onClick={()=>onFollow(post.post_id)}
                aria-pressed={followedIds.includes(post.post_id)}
                aria-label={followedIds.includes(post.post_id) ? 'Unfollow conversation' : 'Follow conversation'}>
                <span aria-hidden="true">{followedIds.includes(post.post_id) ? '✓' : '+'}</span>
                <small>{followedIds.includes(post.post_id) ? 'Following' : 'Follow'}</small>
              </button>
            </div>
          </div>
          <div className="v5-stack-bottom"><span>{active?'▶ Playing':'Video paused'}</span>
            <button type="button" onClick={()=>{const v=players.current.get(key);if(v){v.currentTime=0;void v.play().catch(() => { /* Native playback restrictions. */ });}setReplayToken(n=>n+1)}}>↺ Watch again</button>
            <Link href={'/community/post/'+encodeURIComponent(post.post_id)}>Conversation and replies →</Link>
          </div>
        </article>;
      })}
      <div data-load-sentinel className="v5-stack-end" aria-live="polite">{loading?'Loading more videos…':loadError?<button type="button" onClick={onRetry}>Retry loading videos</button>:hasMore?'More videos load as you scroll':'You are all caught up.'}</div>
    </div>
  );

  const player = (fullScreen: boolean) => (
    <div
      className={'v5-reels-scroll' + (fullScreen ? ' v5-reels-scroll-full' : '')}
      ref={scrollRef}
      role="region"
      aria-label="Vertical grocery videos. Swipe or scroll for the next video."
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'PageDown') {
          event.preventDefault(); moveTo(active + 1);
        } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
          event.preventDefault(); moveTo(active - 1);
        }
      }}
    >
      {clips.map(({ post, media }, index) => {
        const author = getPostAuthorDisplay(post);
        const label = (post.title || post.content || 'Grocery find').trim();
        const complete = [post.title, post.content]
          .filter((value, position, values) => !!value && (position === 0 || value !== values[0]))
          .join('\n\n');
        const key = media.media_id;
        const isActive = index === active;
        return (
          <article className="v5-reel" key={key} data-video-index={index}
            aria-label={'Grocery video by ' + author.name}>
            <div className="v5-reel-stage">
              <video
                ref={element => {
                  if (element) players.current.set(key, element);
                  else players.current.delete(key);
                }}
                src={getVideoPreviewUrl(media.media_url)}
                preload={Math.abs(index - active) <= 1 ? 'metadata' : 'none'}
                playsInline
                autoPlay
                loop
                muted={!soundOn}
                onLoadedMetadata={event => {
                  if (resumeAt.current?.key === key) {
                    try { event.currentTarget.currentTime = resumeAt.current.seconds; }
                    catch { /* The video still plays from its beginning. */ }
                    resumeAt.current = null;
                  }
                }}
                onError={() => setUnsupportedVideo(key)}
                onPlay={() => setNeedsTap(current => current === key ? null : current)}
                onClick={() => fullScreen ? toggleDirectPlayback(key) : expand(index)}
                aria-label={fullScreen ? 'Tap to pause or play the full grocery video' : 'Tap to watch full grocery video'}
              />

              <div className="v5-reel-top">
                <span className="v5-reel-topic">{post.video_topic || 'Grocery finds'}</span>
                <button type="button" aria-label={soundOn ? 'Mute videos' : 'Turn video sound on'}
                  onClick={() => setSoundOn(value => !value)}>
                  {soundOn ? '🔊 Sound on' : '🔇 Sound off'}
                </button>
              </div>

              {!fullScreen && (
                <button
                  className="v5-reel-expand"
                  type="button"
                  ref={isActive ? openButton : undefined}
                  onClick={() => expand(index)}
                  aria-label="Open video in full-screen vertical viewer"
                >
                  <span aria-hidden="true">⛶</span> Full screen
                </button>
              )}
              {fullScreen && isActive && (paused || needsTap === key) && (
                <button type="button" className="v5-reel-play"
                  onClick={() => toggleDirectPlayback(key)} aria-label="Play video">▶</button>
              )}

              {unsupportedVideo===key && <div role="alert" className="v5-video-unsupported">This video cannot play on this device. Try another video.</div>}
              {expandedCaption === key ? (
                <section className="v5-reel-caption-sheet" aria-label="Full video description">
                  <div className="v5-reel-caption-sheet-head">
                    <strong>{author.name}</strong>
                    <button type="button" onClick={() => setExpandedCaption(null)}>Less ↑</button>
                  </div>
                  <p>{complete}</p>
                  <Link href={'/community/post/' + encodeURIComponent(post.post_id)}>
                    Read the conversation and replies
                  </Link>
                </section>
              ) : (
                <div className="v5-reel-caption-min">
                  <strong>{author.name}</strong>
                  <p><span>{label}</span>{complete.length > 45 && (
                    <button type="button" onClick={() => setExpandedCaption(key)}
                      aria-label="Read full video caption">More</button>
                  )}</p>
                </div>
              )}

              <div className="v5-reel-actions">
                <button type="button" className="v5-reel-create" onClick={() => { if (fullScreen) collapse(); onCreateVideo(); }} aria-label="Post a grocery video" title="Post a video">+</button>
                <button type="button"
                  aria-label={liked[post.post_id] ? 'Unlike video' : 'Like video'}
                  aria-pressed={!!liked[post.post_id]}
                  disabled={!!busyLikes[post.post_id]}
                  onClick={() => onLike(post.post_id)}>
                  <span aria-hidden="true">{liked[post.post_id] ? '♥' : '♡'}</span>
                  <small>{post.likes_count} Likes</small>
                </button>
                <button type="button" aria-label="Reply to video"
                  onClick={() => openReply(post)}>
                  <span aria-hidden="true">↪</span><small>{post.comments_count} Replies</small>
                </button>
                <button type="button" aria-label="Share video" onClick={() => onShare(post)}>
                  <span aria-hidden="true">↗</span><small>Share</small>
                </button>
                <button type="button"
                  aria-label={followedIds.includes(post.post_id) ? 'Unfollow conversation' : 'Follow conversation'}
                  aria-pressed={followedIds.includes(post.post_id)}
                  onClick={() => onFollow(post.post_id)}>
                  <span aria-hidden="true">{followedIds.includes(post.post_id) ? '✓' : '+'}</span>
                  <small>{followedIds.includes(post.post_id) ? 'Following' : 'Follow'}</small>
                </button>
              </div>
            </div>
          </article>
        );
      })}
      {!clips.length && !loading && (
        <div className="v5-reels-empty">
          No grocery videos were found. You can explore shopper conversations instead.
        </div>
      )}
    </div>
  );

  const navigation = (
    <div className="v5-reel-nav">
      <button type="button" onClick={() => moveTo(active - 1)} disabled={active === 0}>
        ↑ Previous
      </button>
      <span>Scroll for more videos ↓</span>
      <button type="button" onClick={() => moveTo(active + 1)}
        disabled={active >= clips.length - 1}>Next ↓</button>
    </div>
  );

  return (
    <>
      {!immersive && (
        <div className="v5-reels-wrap">

          {stacked}
        </div>
      )}

      <style jsx global>{`
        .youdle-v5 .v5-reel-actions .v5-reel-create{border:0;background:transparent;box-shadow:none;border-radius:0;color:inherit;font-size:30px;font-weight:800;line-height:1;min-width:40px;min-height:40px;padding:0;display:flex;justify-content:center;align-items:center;cursor:pointer}
      `}</style>
      {immersive && typeof document !== 'undefined' && createPortal(
        <div className="youdle-v5 v5-reel-lightbox" role="dialog" aria-modal="true"
          aria-label="Full-screen grocery videos" ref={overlayRef}>
          <div className="v5-reel-lightbox-heading">
            <strong>Youdle Videos</strong>
            <button type="button" ref={closeButton} className="v5-reel-close"
              onClick={collapse} aria-label="Close full-screen video viewer">✕ Close</button>
          </div>
          {player(true)}
          <div className="v5-reel-lightbox-nav">{navigation}</div>
        </div>, document.body,
      )}
    </>
  );
}
